import { compareVersions } from 'src/modules/boot/versions';

export interface ManifestEntry {
  file_name: string;
  file_hash: string;
  download_url: string;
  size?: number;
}

export interface LatestVersionInfo {
  version: string;
  url?: string;
  min_ios_version_code?: number;
  min_android_version_code?: number;
  data?: { latest?: string };
  manifest?: ManifestEntry[];
  checksum?: string | null;
}

export interface InstalledBundle {
  id: string;
  version: string;
  status: string;
}

export interface DecideUpdateInput {
  currentVersion: string;
  builtinVersion: string;
  platform: string;
  latest: LatestVersionInfo | null;
  installed: InstalledBundle[];
  nativeBuildNumber: number;
  denied: string[];
  pluginVersion?: string;
}

export type UpdateDecision =
  | { kind: 'check_failed' }
  | { kind: 'up_to_date' }
  | { kind: 'no_url' }
  | { kind: 'below_floor'; floor: 'builtin' | 'platform' }
  | { kind: 'denied' }
  | { kind: 'checksum_missing'; version: string }
  | { kind: 'apply_installed'; bundleId: string; version: string; url?: string; checksum?: string }
  | {
      kind: 'download';
      url: string;
      version: string;
      manifest?: ManifestEntry[];
      checksum?: string;
    };

// iOS returns '' and Android may return null for a row without a checksum; the backend validates
// the format, so only presence is decided here.
export const isNonEmptyChecksum = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0;

// Plugin 8.52.0 fails every zip download that lacks a checksum; unknown is treated as requiring,
// which is the safe side of an instant deterministic failure.
export const requiresZipChecksum = (pluginVersion: string | undefined): boolean =>
  pluginVersion === undefined || compareVersions(pluginVersion, '8.52.0') >= 0;

const UNUSABLE_STATUSES = new Set(['error', 'deleted', 'downloading']);

export const decideUpdate = (input: DecideUpdateInput): UpdateDecision => {
  const {
    currentVersion,
    builtinVersion,
    platform,
    latest,
    installed,
    nativeBuildNumber,
    denied,
    pluginVersion,
  } = input;

  // A failed check is NOT evidence of staleness.
  if (!latest) return { kind: 'check_failed' };

  if (compareVersions(latest.version, currentVersion) === 0) return { kind: 'up_to_date' };

  // Floor 1: never run a web bundle older than the one shipped inside the binary — it may not match
  // the native shell's plugin versions.
  if (compareVersions(latest.version, builtinVersion) < 0) {
    return { kind: 'below_floor', floor: 'builtin' };
  }

  // Floor 2: "does this bundle require a newer native binary than I am?" Compares the DEVICE's
  // native build number against the row's minimum — not, as before, two values that both came from
  // the same server response and could only disagree if the server contradicted itself.
  const minCode =
    platform === 'android' ? latest.min_android_version_code : latest.min_ios_version_code;
  if (typeof minCode === 'number' && nativeBuildNumber < minCode) {
    return { kind: 'below_floor', floor: 'platform' };
  }

  // A version that cleared both floors can still be the bundle we just escaped via a revert: it is
  // still on disk with a usable status, because it DID call notifyAppReady() before dying at
  // render.
  if (denied.includes(latest.version)) {
    return { kind: 'denied' };
  }

  // Reuse a bundle already on disk before spending bytes. `set()` only requires the bundle to
  // exist; it does not inspect status.
  const existing = installed.find(
    (b) => b.version === latest.version && !UNUSABLE_STATUSES.has(b.status),
  );
  const checksumMissing =
    requiresZipChecksum(pluginVersion) && !isNonEmptyChecksum(latest.checksum);
  if (existing) {
    return {
      kind: 'apply_installed',
      bundleId: existing.id,
      version: latest.version,
      // Without url a rejected set() takes the missing-url path instead of a doomed re-download.
      ...(!checksumMissing && { url: latest.url }),
      ...(isNonEmptyChecksum(latest.checksum) && { checksum: latest.checksum }),
    };
  }

  if (!latest.url) return { kind: 'no_url' };

  if (checksumMissing) return { kind: 'checksum_missing', version: latest.version };

  return {
    kind: 'download',
    url: latest.url,
    version: latest.version,
    manifest: latest.manifest,
    ...(isNonEmptyChecksum(latest.checksum) && { checksum: latest.checksum }),
  };
};

export const computeShouldUpdateNativeApp = (
  latest: LatestVersionInfo | null,
): boolean => {
  const globalLatest = latest?.data?.latest;
  if (!latest || !globalLatest) return false;
  return latest.version !== globalLatest;
};
